import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area, RadialBarChart, RadialBar } from 'recharts';
import Navigation from '../components/Navigation';
import Header from '../components/Header';
import NotificationToaster from '../components/NotificationToaster';
import Login from '../components/Login';
import {
  apiService
} from "../services/componentServices";

const COLORS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
  'linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%)',
  'linear-gradient(135deg, #d4fc79 0%, #96e6a1 100%)',
  'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)'
];

const SOLID_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FF6B6B', '#48DBFB', '#F368E0', '#1DD1A1'];

// Currency symbol mapping function
const getCurrencySymbol = (currencyCode = null, currentGlobalCurrency = 'USD') => {
  const currencies = [
    { code: 'INR', symbol: '₹' },
    { code: 'USD', symbol: '$' },
    { code: 'EUR', symbol: '€' },
    { code: 'GBP', symbol: '£' },
    { code: 'JPY', symbol: '¥' },
    { code: 'CAD', symbol: 'C$' },
    { code: 'AUD', symbol: 'A$' },
    { code: 'AED', symbol: 'د.إ' },
    { code: 'SAR', symbol: '﷼' },
    { code: 'QAR', symbol: '﷼' },
    { code: 'KWD', symbol: 'د.ك' },
    { code: 'BHD', symbol: '.د.ب' },
    { code: 'OMR', symbol: '﷼' }
  ];
  return currencies.find(c => c.code === (currencyCode || currentGlobalCurrency))?.symbol || '$';
};

const LogoutConfirmModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  const handleConfirmLogout = () => {
    localStorage.removeItem('blendOptimizerCurrentUser');
    localStorage.removeItem('blendOptimizerModelBasis');
    localStorage.removeItem('blendOptimizerCurrency');
    localStorage.removeItem('blendOptimizerVolumeUnit');
    localStorage.removeItem('blendOptimizerMassUnit');

    if (onConfirm) {
      onConfirm();
    }
    window.location.reload();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full transform transition-all">
        <div className="p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Confirm Logout</h3>
              <p className="text-gray-600 mt-1">Are you sure you want to logout?</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-6">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmLogout}
              className="flex-1 px-4 py-3 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const useLogout = () => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    localStorage.removeItem('blendOptimizerCurrentUser');
    localStorage.removeItem('blendOptimizerModelBasis');
    localStorage.removeItem('blendOptimizerCurrency');
    localStorage.removeItem('blendOptimizerVolumeUnit');
    localStorage.removeItem('blendOptimizerMassUnit');
    window.location.reload();
  };

  const handleCancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return {
    showLogoutConfirm,
    handleLogoutClick,
    handleConfirmLogout,
    handleCancelLogout
  };
};

const ComponentsAndResultsPage = ({
  currentUser,
  onLogout,
  onShowUserManagement,
  onTabChange,
  activeTab,
  sidebarOpen,
  onToggleSidebar,
  currency
}) => {
  const [savedComponents, setSavedComponents] = useState([]);
  const [savedResults, setSavedResults] = useState([]);
  const [savedProperties, setSavedProperties] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [resultsSearchTerm, setResultsSearchTerm] = useState('');
  const [propertiesSearchTerm, setPropertiesSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  const [detailView, setDetailView] = useState(false);
  const [propertyDetailView, setPropertyDetailView] = useState(false);
  const [customColumns, setCustomColumns] = useState([]);
  const [componentsCurrentPage, setComponentsCurrentPage] = useState(1);
  const [resultsCurrentPage, setResultsCurrentPage] = useState(1);
  const [propertiesCurrentPage, setPropertiesCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [loading, setLoading] = useState({
    components: false,
    results: false,
    properties: false,
    applyComponents: false,
    applyProperties: false,
    applyAll: false,
    deleteResult: false,
    deleteComponent: false,
    deleteProperty: false,
    editResult: false
  });
  const [chartType, setChartType] = useState('pie');
  const [isMobile, setIsMobile] = useState(false);
  const [isEditingResult, setIsEditingResult] = useState(false);
  const [editingResultData, setEditingResultData] = useState(null);
  const [editItemData, setEditItemData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleConfirmLogout,
    handleCancelLogout
  } = useLogout();

  useEffect(() => {
    const handleRefreshResults = () => {
      if (activeTab === 'optimization-results') {
        loadResultsFromMongoDB();
      } else if (activeTab === 'components') {
        loadComponentsFromMongoDB();
      } else if (activeTab === 'properties') {
        loadPropertiesFromMongoDB();
      }
    };

    window.addEventListener('refreshResults', handleRefreshResults);

    return () => {
      window.removeEventListener('refreshResults', handleRefreshResults);
    };
  }, [activeTab]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  useEffect(() => {
    const savedCustomColumns = localStorage.getItem('blendOptimizerCustomColumns');
    if (savedCustomColumns) {
      setCustomColumns(JSON.parse(savedCustomColumns));
    }

    if (activeTab === 'components') {
      loadComponentsFromMongoDB();
    } else if (activeTab === 'properties') {
      loadPropertiesFromMongoDB();
    } else if (activeTab === 'optimization-results') {
      loadResultsFromMongoDB();
    }
  }, [activeTab]);

  const loadResultsFromMongoDB = async () => {
    setLoading(prev => ({ ...prev, results: true }));
    try {
      const response = await apiService.optimizedResults.getResults({ page: 1, limit: 1000 });
      if (response.success) {
        const results = response.data || [];

        const transformedResults = results.map(result => {
          const blendData = result.blend || {};

          // Transform components with property values from componentPropertyValues
          const transformedComponents = result.components ? result.components.map(comp => {
            // First, try to get density from componentPropertyValues (actual entered value)
            let actualDensity = undefined;
            if (result.componentPropertyValues && Array.isArray(result.componentPropertyValues)) {
              const compPropValue = result.componentPropertyValues.find(
                cpv => {
                  const cpvName = (cpv.componentName || '').toLowerCase().trim();
                  const compName = (comp.originalName || comp.name || '').toLowerCase().trim();
                  return cpvName === compName;
                }
              );
              if (compPropValue && compPropValue.propertyValues && compPropValue.propertyValues.density !== undefined) {
                actualDensity = compPropValue.propertyValues.density;
              }
            }

            // If not found in componentPropertyValues, use comp.density (preserve actual value, including 0)
            if (actualDensity === undefined) {
              // Preserve null/empty as empty string, but keep valid numbers (including 0)
              if (comp.density === null || comp.density === undefined || comp.density === '') {
                actualDensity = '';
              } else {
                actualDensity = comp.density;
              }
            }

            // Convert null/undefined to empty string for display
            if (actualDensity === null || actualDensity === undefined) {
              actualDensity = '';
            }

            const baseComponent = {
              name: comp.originalName || comp.name,
              cost: comp.cost,
              min: comp.min || comp.minVolume,
              max: comp.max || comp.maxVolume,
              availability: comp.availability || comp.availableVolume,
              density: actualDensity, // Use actual entered density value
              gravity: comp.gravity || 0,
              basis: comp.basis || 'volume',
              blending_type: comp.blending_type || 'linear',
              costUnit: comp.costUnit || 'USD',
              volumeUnit: comp.volumeUnit || 'bbl'
            };

            // Add property values from componentPropertyValues if available (excluding density as we already handled it)
            if (result.componentPropertyValues && Array.isArray(result.componentPropertyValues)) {
              const compPropValue = result.componentPropertyValues.find(
                cpv => {
                  const cpvName = (cpv.componentName || '').toLowerCase().trim();
                  const compName = (comp.originalName || comp.name || '').toLowerCase().trim();
                  return cpvName === compName;
                }
              );
              if (compPropValue && compPropValue.propertyValues) {
                Object.keys(compPropValue.propertyValues).forEach(propName => {
                  // Skip density as we already handled it above
                  if (propName.toLowerCase() === 'density') return;

                  const propValue = compPropValue.propertyValues[propName];
                  // Store the property value with the exact property name from componentPropertyValues
                  if (propValue !== undefined && propValue !== null) {
                    baseComponent[propName] = Number(propValue) || 0;
                  }
                });
              }
            }

            // Also check if component has direct property fields (for backward compatibility)
            Object.keys(comp).forEach(key => {
              const normalizedKey = String(key || '').toLowerCase();
              const fixedFields = ['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'minvolume', 'maxvolume', 'availability', 'availablevolume', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype', '_id', 'id'];
              if (!fixedFields.includes(normalizedKey) && comp[key] !== undefined && comp[key] !== null && !baseComponent.hasOwnProperty(key)) {
                baseComponent[key] = Number(comp[key]) || 0;
              }
            });

            // Also add from otherProductList if available (for backward compatibility)
            // But don't overwrite density as we already have the actual value
            if (comp.otherProductList && Array.isArray(comp.otherProductList)) {
              comp.otherProductList.forEach(product => {
                if (product.name && product.name.toLowerCase() !== 'density' && !baseComponent.hasOwnProperty(product.name)) {
                  baseComponent[product.name] = product.value || 0;
                }
              });
            }

            return baseComponent;
          }) : [];

          const transformedPropertySpecs = result.constraintsProperties ? result.constraintsProperties.map(prop => ({
            property: prop.originalName || prop.property || prop.name,
            uom: prop.uom || prop.volumeUnit,
            min: prop.min || prop.minVolume,
            max: prop.max || prop.maxVolume,
            basis: prop.basis || 'volume',
            linear: prop.linear,
            index_formula: prop.indexFormula || prop.index_formula,
            reverse_formula: prop.reverseFormula || prop.reverse_formula,
            actualResult: prop.actualResult || 0
          })) : [];

          let finalCostBreakdown = [];

          if (result.costBreakdown && result.costBreakdown.length > 0) {
            finalCostBreakdown = result.costBreakdown.map(item => {
              let componentName = 'Unknown Component';

              const blendComponentName = Object.keys(blendData).find(key =>
                key.toLowerCase() === (item.componentName || item.name || '').toLowerCase()
              );

              const componentFromList = transformedComponents.find(comp =>
                comp.name.toLowerCase() === (item.componentName || item.name || '').toLowerCase()
              );

              if (!blendComponentName && !componentFromList) {
                const volumeMatch = Object.entries(blendData).find(([name, volume]) =>
                  Math.abs(volume - item.volume) < 0.01
                );
                if (volumeMatch) {
                  componentName = volumeMatch[0];
                }
              } else {
                componentName = blendComponentName || componentFromList?.name || item.componentName || item.name || 'Unknown Component';
              }

              return {
                name: componentName,
                volume: item.volume,
                unitCost: item.unitCost,
                totalCost: item.totalCost
              };
            });
          } else {
            finalCostBreakdown = Object.entries(blendData)
              .filter(([_, volume]) => volume > 0)
              .map(([name, volume]) => {
                const component = transformedComponents.find(comp =>
                  comp.name.toLowerCase() === name.toLowerCase()
                );
                return {
                  name: name,
                  volume: Number(volume),
                  unitCost: component ? component.cost : 0,
                  totalCost: component ? (component.cost * Number(volume)) : 0
                };
              });
          }

          return {
            _id: result._id,
            customName: result.optimizeName || result.originalOptimizeName || `Optimization ${new Date(result.createdAt).toLocaleDateString()}`,
            customTextField: result.remarks,
            timestamp: new Date(result.createdAt).getTime(),
            status: result.status,
            totalCost: result.totalCost,
            blend: blendData,
            components: transformedComponents,
            propertySpecs: transformedPropertySpecs,
            costBreakdown: finalCostBreakdown,
            modelBasis: result.modelBasis,
            currency: result.currency,
            blendingType: result.blendingType,
            selectedPropertyColumns: result.selectedPropertyColumns || [],
            componentPropertyValues: result.componentPropertyValues || []
          };
        });

        console.log("📥 Loaded results with actualResults:", transformedResults.map(r => ({
          name: r.customName,
          properties: r.propertySpecs.map(p => ({
            property: p.property,
            actualResult: p.actualResult
          }))
        })));

        setSavedResults(transformedResults);
        if (transformedResults.length > 0 && !selectedResult) {
          setSelectedResult(transformedResults[0]);
        }
      } else {
        throw new Error(response.message || 'Failed to load results');
      }
    } catch (error) {
      console.error('Error loading results from MongoDB:', error);
      addNotification("error", "Failed to load results from database");
    } finally {
      setLoading(prev => ({ ...prev, results: false }));
    }
  };

  const loadComponentsFromMongoDB = async () => {
    setLoading(prev => ({ ...prev, components: true }));
    try {
      const response = await apiService.components.getAll();
      if (response.success) {
        const components = response.data || [];

        const transformedComponents = components.map(comp => ({
          _id: comp._id,
          customName: comp.name || `Component Set ${new Date(comp.createdAt).toLocaleDateString()}`,
          timestamp: new Date(comp.createdAt).getTime(),
          components: [{
            name: comp.name,
            cost: comp.cost,
            min: comp.minVolume,
            max: comp.maxVolume,
            availability: comp.availableVolume,
            density: comp.density,
            ...(comp.otherProductList && comp.otherProductList.reduce((acc, product) => {
              acc[product.name] = product.value;
              return acc;
            }, {}))
          }],
          modelBasis: comp.basis || 'volume',
          currency: comp.costUnit || 'USD'
        }));

        setSavedComponents(transformedComponents);
        addNotification("success", `Loaded ${transformedComponents.length} components from database`);
      } else {
        throw new Error(response.message || 'Failed to load components');
      }
    } catch (error) {
      console.error('Error loading components from MongoDB:', error);
      addNotification("error", "Failed to load components from database");
    } finally {
      setLoading(prev => ({ ...prev, components: false }));
    }
  };

  const loadPropertiesFromMongoDB = async () => {
    setLoading(prev => ({ ...prev, properties: true }));
    try {
      const response = await apiService.gradeProperties.getList({ page: 1, limit: 1000 });
      if (response.success) {
        const properties = response.data || [];

        const transformedProperties = properties.map(prop => ({
          _id: prop._id,
          customName: prop.gradeName || `Property Set ${new Date(prop.createdAt).toLocaleDateString()}`,
          timestamp: new Date(prop.createdAt).getTime(),
          properties: prop.constraintsProperties ? prop.constraintsProperties.map(constraint => ({
            property: constraint.name,
            uom: constraint.volumeUnit,
            min: constraint.minVolume,
            max: constraint.maxVolume,
            basis: 'volume',
            linear: constraint.linear,
            index_formula: constraint.indexFormula,
            reverse_formula: constraint.reverseFormula
          })) : []
        }));

        setSavedProperties(transformedProperties);
        addNotification("success", `Loaded ${transformedProperties.length} property sets from database`);
      } else {
        throw new Error(response.message || 'Failed to load properties');
      }
    } catch (error) {
      console.error('Error loading properties from MongoDB:', error);
      addNotification("error", "Failed to load properties from database");
    } finally {
      setLoading(prev => ({ ...prev, properties: false }));
    }
  };

  const applyComponentsToOptimizer = async (componentData) => {
    setLoading(prev => ({ ...prev, applyComponents: true }));
    try {
      const componentsToSave = {
        components: componentData.components.map(comp => {
          const cleanComponent = {
            name: comp.name || '',
            cost: comp.cost || '',
            min: comp.min || '',
            max: comp.max || '',
            availability: comp.availability || 1000,
            density: comp.density || '',
            gravity: comp.gravity || 0,
            basis: comp.basis || 'volume',
            blending_type: comp.blending_type || 'linear',
            costUnit: comp.costUnit || 'USD',
            volumeUnit: comp.volumeUnit || 'bbl'
          };

          // Add all custom property values (excluding fixed fields)
          Object.keys(comp).forEach(key => {
            const normalizedKey = key.toLowerCase();
            const fixedFields = ['name', 'originalname', 'cost', 'costunit', 'min', 'max', 'minvolume', 'maxvolume', 'availability', 'availablevolume', 'volumeunit', 'density', 'gravity', 'basis', 'blending_type', 'blendingtype', '_id', 'id'];
            if (!fixedFields.includes(normalizedKey) && comp[key] !== undefined && comp[key] !== null && !cleanComponent.hasOwnProperty(key)) {
              cleanComponent[key] = Number(comp[key]) || 0;
            }
          });

          return cleanComponent;
        }),
        customName: componentData.customName || `Component Set ${new Date().toLocaleDateString()}`,
        timestamp: componentData.timestamp,
        modelBasis: componentData.modelBasis || 'volume',
        selectedPropertyColumns: componentData.selectedPropertyColumns || []
      };

      localStorage.setItem('componentsToLoad', JSON.stringify(componentsToSave));
      addNotification("success", "Components loaded successfully! Switching to optimizer...");

      setTimeout(() => {
        window.location.reload();
      }, 500);

      setTimeout(() => {
        onTabChange('optimizer');
      }, 1000);

    } catch (error) {
      console.error('Error applying components:', error);
      addNotification("error", "Failed to load components");
    } finally {
      setLoading(prev => ({ ...prev, applyComponents: false }));
    }
  };

  const applyPropertiesToOptimizer = async (propertyData) => {
    setLoading(prev => ({ ...prev, applyProperties: true }));
    try {
      if (propertyData.properties && propertyData.properties.length > 0) {
        const propertiesToSave = {
          properties: propertyData.properties.map(prop => ({
            property: prop.property || '',
            uom: prop.uom || '',
            min: prop.min || '',
            max: prop.max || '',
            basis: prop.basis || 'volume',
            linear: prop.linear,
            index_formula: prop.index_formula || '',
            reverse_formula: prop.reverse_formula || ''
          })),
          customName: propertyData.customName || `Property Set ${new Date().toLocaleDateString()}`,
          timestamp: propertyData.timestamp
        };

        localStorage.setItem('propertiesToLoad', JSON.stringify(propertiesToSave));
      }

      if (propertyData.customTextField) {
        localStorage.setItem('customNotesToLoad', propertyData.customTextField);
      }

      addNotification("success", "Properties loaded successfully! Switching to optimizer...");

      setTimeout(() => {
        window.location.reload();
      }, 500);

      setTimeout(() => {
        onTabChange('optimizer');
      }, 1000);

    } catch (error) {
      console.error('Error applying properties:', error);
      addNotification("error", "Failed to load properties");
    } finally {
      setLoading(prev => ({ ...prev, applyProperties: false }));
    }
  };


  const applyAllToOptimizer = async (result) => {
    setLoading(prev => ({ ...prev, applyAll: true }));
    try {
      const resultDataForEdit = {
        ...result,
        constraintsProperties: result.propertySpecs ? result.propertySpecs.map(prop => ({
          name: prop.property || '',
          originalName: prop.property || '',
          property: prop.property || '',
          uom: prop.uom || '',
          min: prop.min || 0,
          max: prop.max || 100,
          basis: prop.basis || 'volume',
          linear: prop.linear !== undefined ? prop.linear : true,
          indexFormula: prop.index_formula || '',
          reverseFormula: prop.reverse_formula || '',
          index_formula: prop.index_formula || '',
          reverse_formula: prop.reverse_formula || '',
          actualResult: prop.actualResult || 0
        })) : [],
        // Ensure componentPropertyValues is included
        componentPropertyValues: result.componentPropertyValues || []
      };

      const editData = {
        type: 'edit',
        resultData: resultDataForEdit,
        itemId: result._id,
        itemType: 'result'
      };

      localStorage.setItem('blendOptimizerEditData', JSON.stringify(editData));

      addNotification("success", `Loading "${result.customName || result.customTextField}" for editing... Switching to optimizer.`);

      // Immediately switch to optimizer tab - same as handleEditResult
      onTabChange('optimizer');

    } catch (error) {
      console.error('Error applying all data:', error);
      addNotification("error", "Failed to load data");
    } finally {
      setLoading(prev => ({ ...prev, applyAll: false }));
    }
  };




  const handleEditResult = (result) => {
    const resultDataForEdit = {
      ...result,
      // Ensure constraintsProperties is set from propertySpecs
      constraintsProperties: result.propertySpecs ? result.propertySpecs.map(prop => ({
        name: prop.property || '',
        originalName: prop.property || '',
        property: prop.property || '',
        uom: prop.uom || '',
        min: prop.min || 0,
        max: prop.max || 100,
        basis: prop.basis || 'volume',
        linear: prop.linear !== undefined ? prop.linear : true,
        indexFormula: prop.index_formula || '',
        reverseFormula: prop.reverse_formula || '',
        index_formula: prop.index_formula || '',
        reverse_formula: prop.reverse_formula || '',
        actualResult: prop.actualResult || 0
      })) : [],
      componentPropertyValues: result.componentPropertyValues || []
    };

    const editData = {
      type: 'edit',
      resultData: resultDataForEdit,
      itemId: result._id,
      itemType: 'result'
    };

    localStorage.setItem('blendOptimizerEditData', JSON.stringify(editData));

    addNotification("success", `Loading "${result.customName}" for editing... Switching to optimizer.`);

    onTabChange('optimizer');
  };


  const handleEditComponent = (componentData) => {
    const editData = {
      type: 'edit',
      componentData: componentData,
      itemId: componentData._id,
      itemType: 'component'
    };

    localStorage.setItem('blendOptimizerEditData', JSON.stringify(editData));

    if (componentData.components && componentData.components.length > 0) {
      const componentsToEdit = {
        components: componentData.components.map(comp => {
          const cleanComp = {
            name: comp.name || '',
            cost: comp.cost || '',
            min: comp.min || '',
            max: comp.max || '',
            availability: comp.availability || 1000,
            density: comp.density || '',
            gravity: comp.gravity || '',
            basis: comp.basis || 'volume'
          };

          // Add all custom properties
          Object.keys(comp).forEach(key => {
            if (!['name', 'cost', 'min', 'max', 'availability', 'density', 'gravity', 'basis', 'blending_type'].includes(key.toLowerCase())) {
              cleanComp[key] = comp[key] || 0;
            }
          });

          return cleanComp;
        }),
        customName: componentData.customName,
        selectedPropertyColumns: componentData.selectedPropertyColumns || [],
        modelBasis: componentData.modelBasis || 'volume'
      };

      localStorage.setItem('componentsToEdit', JSON.stringify(componentsToEdit));
    }

    addNotification("success", `Loading "${componentData.customName}" for editing... Switching to optimizer.`);

    setTimeout(() => {
      onTabChange('optimizer');
    }, 500);
  };




  const handleEditProperty = (propertyData) => {
    const editData = {
      type: 'edit',
      propertyData: propertyData,
      itemId: propertyData._id,
      itemType: 'property'
    };

    localStorage.setItem('blendOptimizerEditData', JSON.stringify(editData));

    // Store property data for editing
    if (propertyData.properties && propertyData.properties.length > 0) {
      const propertiesToEdit = {
        properties: propertyData.properties.map(prop => ({
          property: prop.property || '',
          uom: prop.uom || '',
          min: prop.min || '',
          max: prop.max || '',
          basis: prop.basis || 'volume',
          linear: prop.linear,
          index_formula: prop.index_formula || '',
          reverse_formula: prop.reverse_formula || ''
        })),
        customName: propertyData.customName
      };

      localStorage.setItem('propertiesToEdit', JSON.stringify(propertiesToEdit));
    }

    addNotification("success", `Loading "${propertyData.customName}" for editing... Switching to optimizer.`);

    setTimeout(() => {
      onTabChange('optimizer');
    }, 500);
  };


  const saveEditedResult = async () => {
    if (!editingResultData) return;

    setLoading(prev => ({ ...prev, editResult: true }));
    try {
      const updatedResult = {
        ...editingResultData,
        customName: editingResultData.customName || editingResultData.customTextField || `Optimization ${formatDate(editingResultData.timestamp)}`,
        updatedAt: new Date().toISOString()
      };

      const response = await apiService.optimizedResults.update(editingResultData._id, updatedResult);

      if (response.success) {
        setSavedResults(prev =>
          prev.map(result =>
            result._id === editingResultData._id ? updatedResult : result
          )
        );
        setSelectedResult(updatedResult);
        setIsEditingResult(false);
        setEditingResultData(null);
        addNotification("success", "Result updated successfully");
      } else {
        throw new Error(response.message || 'Failed to update result');
      }
    } catch (error) {
      console.error('Error updating result:', error);
      addNotification("error", "Failed to update result");
    } finally {
      setLoading(prev => ({ ...prev, editResult: false }));
    }
  };

  const cancelEditResult = () => {
    setIsEditingResult(false);
    setEditingResultData(null);
    addNotification("info", "Edit cancelled");
  };

  const addNotification = (type, message) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message }]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const prepareChartData = (blendData) => {
    if (!blendData || typeof blendData !== 'object') return [];

    return Object.entries(blendData)
      .filter(([_, volume]) => volume > 0)
      .map(([name, volume], index) => ({
        name,
        volume: Number(volume),
        value: Number(volume),
        percentage: 0,
        fill: SOLID_COLORS[index % SOLID_COLORS.length],
        gradient: COLORS[index % COLORS.length]
      }));
  };

  const prepareEnhancedChartData = (blendData) => {
    const baseData = prepareChartData(blendData);
    const totalVolume = baseData.reduce((sum, item) => sum + item.volume, 0);

    return baseData.map(item => {
      const component = selectedResult?.components?.find(c => c.name === item.name);
      return {
        ...item,
        percentage: totalVolume > 0 ? (item.volume / totalVolume * 100) : 0,
        unitCost: component ? component.cost : 0,
        totalCost: component ? (component.cost * item.volume) : 0,
        currency: selectedResult?.currency
      };
    });
  };

  const deleteResult = async (resultId) => {
    if (window.confirm("Are you sure you want to delete this optimization result? This action cannot be undone.")) {
      setLoading(prev => ({ ...prev, deleteResult: true }));
      try {
        const response = await apiService.optimizedResults.delete(resultId);
        if (response.success) {
          setSavedResults(prev => prev.filter(result => result._id !== resultId));
          if (selectedResult && selectedResult._id === resultId) {
            if (savedResults.length > 1) {
              setSelectedResult(savedResults.find(result => result._id !== resultId));
            } else {
              setSelectedResult(null);
            }
          }
          addNotification("success", "Result deleted successfully");
          loadResultsFromMongoDB();
        } else {
          throw new Error(response.message || 'Failed to delete result');
        }
      } catch (error) {
        console.error('Error deleting result:', error);
        addNotification("error", "Failed to delete result");
      } finally {
        setLoading(prev => ({ ...prev, deleteResult: false }));
      }
    }
  };

  const handleDeleteComponent = async (componentId) => {
    if (window.confirm("Are you sure you want to delete this component set? This action cannot be undone.")) {
      setLoading(prev => ({ ...prev, deleteComponent: true }));
      try {
        const response = await apiService.components.delete(componentId);
        if (response.success) {
          setSavedComponents(prev => prev.filter(component => component._id !== componentId));
          if (selectedComponent && selectedComponent._id === componentId) {
            setSelectedComponent(null);
            setDetailView(false);
          }
          addNotification("success", "Component deleted successfully");
          loadComponentsFromMongoDB();
        } else {
          throw new Error(response.message || 'Failed to delete component');
        }
      } catch (error) {
        console.error('Error deleting component:', error);
        addNotification("error", "Failed to delete component");
      } finally {
        setLoading(prev => ({ ...prev, deleteComponent: false }));
      }
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (window.confirm("Are you sure you want to delete this property set? This action cannot be undone.")) {
      setLoading(prev => ({ ...prev, deleteProperty: true }));
      try {
        const response = await apiService.gradeProperties.delete(propertyId);
        if (response.success) {
          setSavedProperties(prev => prev.filter(property => property._id !== propertyId));
          if (selectedProperty && selectedProperty._id === propertyId) {
            setSelectedProperty(null);
            setPropertyDetailView(false);
          }
          addNotification("success", "Property set deleted successfully");
          loadPropertiesFromMongoDB();
        } else {
          throw new Error(response.message || 'Failed to delete property');
        }
      } catch (error) {
        console.error('Error deleting property:', error);
        addNotification("error", "Failed to delete property");
      } finally {
        setLoading(prev => ({ ...prev, deleteProperty: false }));
      }
    }
  };

  const handleExportResultsCSV = () => {
    if (savedResults.length === 0) {
      addNotification("warning", "No data to export");
      return;
    }

    try {
      // If a specific result is selected, export only that one
      const resultsToExport = selectedResult ? [selectedResult] : savedResults;

      let csvContent = "data:text/csv;charset=utf-8,";

      // SECTION 1: OVERVIEW SECTION
      csvContent += "OPTIMIZATION OVERVIEW SECTION\n";
      csvContent += "Metric,Value\n";

      resultsToExport.forEach((result, resultIndex) => {
        const totalVolume = Object.values(result.blend || {}).reduce((a, b) => a + Number(b || 0), 0);
        const componentCount = result.components?.length || 0;
        const propertyCount = result.propertySpecs?.length || 0;
        const activeComponents = Object.entries(result.blend || {}).filter(([_, volume]) => volume > 0).length;

        // Overview data
        const overviewData = [
          ["Optimization Name", result.customName || result.optimizeName || 'Optimization'],
          ["Timestamp", formatDate(result.timestamp)],
          ["Status", result.status || 'Success'],
          ["Total Cost", result.totalCost || 0],
          ["Total Volume", totalVolume],
          ["Currency", result.currency || currency || 'USD'],
          ["Model Basis", result.modelBasis || 'volume'],
          ["Blending Type", result.blendingType || 'linear'],
          ["Total Components", componentCount],
          ["Active Components", activeComponents],
          ["Properties Constrained", propertyCount],
          ["Custom Notes", result.customTextField || result.remarks || ''],
          ["Average Unit Cost", totalVolume > 0 ? (result.totalCost / totalVolume).toFixed(4) : 0]
        ];

        overviewData.forEach(([metric, value]) => {
          csvContent += `"${metric}","${value}"\n`;
        });

        csvContent += "\n";

        // SECTION 2: BLEND COMPOSITION
        csvContent += "BLEND COMPOSITION SECTION\n";
        csvContent += "Component Name,Final Volume,Composition %,Unit Cost,Total Cost Contribution\n";

        Object.entries(result.blend || {})
          .filter(([_, volume]) => volume > 0)
          .forEach(([name, volume]) => {
            const percentage = totalVolume > 0 ? ((volume / totalVolume) * 100) : 0;
            const component = (result.components || []).find(c =>
              (c.name || '').toLowerCase() === (name || '').toLowerCase()
            );
            const costItem = (result.costBreakdown || []).find(c =>
              c.name?.toLowerCase() === name?.toLowerCase()
            );
            const unitCost = component?.cost || costItem?.unitCost || 0;
            const totalCostContrib = costItem?.totalCost || (unitCost ? unitCost * volume : 0);

            csvContent += `"${name}","${Number(volume).toFixed(2)}","${percentage.toFixed(2)}%","${unitCost}","${totalCostContrib.toFixed(2)}"\n`;
          });

        csvContent += "\n";

        // SECTION 3: COMPONENTS DETAILS
        csvContent += "COMPONENTS DETAILS SECTION\n";

        // Get all unique property keys from all components
        const getAllComponentPropertyKeys = () => {
          const allKeys = new Set();
          const fixedFields = new Set(["name", "originalname", "cost", "costunit", "min", "max", "availability", "availablevolume", "volumeunit", "density", "gravity", "basis", "blending_type", "blendingtype", "_id", "id"]);

          (result.components || []).forEach(component => {
            Object.keys(component).forEach(key => {
              const normalizedKey = key.toLowerCase();
              if (!fixedFields.has(normalizedKey) && component[key] !== undefined && component[key] !== null) {
                allKeys.add(key);
              }
            });
          });

          return Array.from(allKeys).sort();
        };

        const componentPropertyKeys = getAllComponentPropertyKeys();

        // Create headers for components table
        const componentHeaders = [
          'Component Name', 'Cost', 'Cost Unit', 'Min Volume', 'Max Volume', 'Availability',
          'Density', 'Gravity', 'Basis', 'Blending Type', 'Volume Unit', 'Optimized Volume', 'Used in Solution'
        ];

        // Add dynamic property columns
        componentPropertyKeys.forEach(prop => {
          componentHeaders.push(prop);
        });

        csvContent += componentHeaders.map(h => `"${h}"`).join(',') + '\n';

        // Add component data rows
        (result.components || []).forEach(component => {
          const componentName = component.name || component.originalName || '';
          const optimizedVolume = result.blend && result.blend[componentName] ? Number(result.blend[componentName]) : 0;

          const row = [
            `"${componentName}"`,
            `"${component.cost !== undefined && component.cost !== null ? component.cost : ''}"`,
            `"${component.costUnit || result.currency || currency || 'USD'}"`,
            `"${component.min !== undefined && component.min !== null ? component.min : (component.minVolume || '')}"`,
            `"${component.max !== undefined && component.max !== null ? component.max : (component.maxVolume || '')}"`,
            `"${component.availability !== undefined && component.availability !== null ? component.availability : (component.availableVolume || '')}"`,
            `"${component.density !== undefined && component.density !== null ? component.density : ''}"`,
            `"${component.gravity !== undefined && component.gravity !== null ? component.gravity : ''}"`,
            `"${component.basis || 'volume'}"`,
            `"${component.blending_type || component.blendingType || 'linear'}"`,
            `"${component.volumeUnit || 'bbl'}"`,
            `"${optimizedVolume.toFixed(2)}"`,
            `"${optimizedVolume > 0 ? 'YES' : 'NO'}"`
          ];

          // Add dynamic property values
          componentPropertyKeys.forEach(prop => {
            const value = component[prop];
            row.push(`"${value !== undefined && value !== null ? value : ''}"`);
          });

          csvContent += row.join(',') + '\n';
        });

        csvContent += "\n";

        // SECTION 4: PROPERTIES & CONSTRAINTS
        csvContent += "PROPERTIES & CONSTRAINTS SECTION\n";
        csvContent += "Property Name,UOM,Min Constraint,Max Constraint,Actual Result,Basis,Linearity,Index Formula,Reverse Formula,Constraint Status\n";

        const allPropertySpecs = [
          ...(result.propertySpecs || []),
          ...(result.constraintsProperties || [])
        ];

        allPropertySpecs.forEach(prop => {
          const propName = prop.property || prop.name || '';
          const actualResult = prop.actualResult !== undefined && prop.actualResult !== null ?
            Number(prop.actualResult) : null;
          const min = prop.min !== undefined && prop.min !== null && prop.min !== '' ?
            Number(prop.min) : null;
          const max = prop.max !== undefined && prop.max !== null && prop.max !== '' ?
            Number(prop.max) : null;

          const constraintStatus = (() => {
            if (actualResult === null) return 'No Result';
            if (min !== null && actualResult < (min - 0.0001)) return 'VIOLATED (Below Min)';
            if (max !== null && actualResult > (max + 0.0001)) return 'VIOLATED (Above Max)';
            if (min !== null || max !== null) return 'SATISFIED';
            return 'No Constraints';
          })();

          csvContent += [
            `"${propName}"`,
            `"${prop.uom || prop.volumeUnit || ''}"`,
            `"${min !== null ? min : ''}"`,
            `"${max !== null ? max : ''}"`,
            `"${actualResult !== null ? actualResult.toFixed(6) : ''}"`,
            `"${prop.basis || 'volume'}"`,
            `"${prop.linear !== undefined ? (prop.linear ? 'Linear' : 'Non-Linear') : 'Linear'}"`,
            `"${prop.index_formula || prop.indexFormula || ''}"`,
            `"${prop.reverse_formula || prop.reverseFormula || ''}"`,
            `"${constraintStatus}"`
          ].join(',') + '\n';
        });

        csvContent += "\n";

        // SECTION 5: COST BREAKDOWN
        csvContent += "COST BREAKDOWN SECTION\n";
        csvContent += "Component,Volume,Unit Cost,Total Cost,Cost %\n";

        const generateCostBreakdown = () => {
          if (result.costBreakdown && result.costBreakdown.length > 0) {
            const validBreakdown = result.costBreakdown.filter(item =>
              item.name && item.name !== 'Unknown Component' && item.volume > 0
            );

            if (validBreakdown.length > 0) {
              return validBreakdown;
            }
          }

          // Fallback: generate from blend data
          const blendData = result.blend || {};
          const components = result.components || [];

          return Object.entries(blendData)
            .filter(([_, volume]) => volume > 0)
            .map(([name, volume]) => {
              const component = components.find(comp =>
                (comp.name || '').toLowerCase() === (name || '').toLowerCase()
              );
              return {
                name: name,
                volume: Number(volume),
                unitCost: component ? component.cost : 0,
                totalCost: component ? (component.cost * Number(volume)) : 0
              };
            });
        };

        const costBreakdown = generateCostBreakdown();
        const totalCost = costBreakdown.reduce((sum, item) => sum + (item.totalCost || 0), 0);

        costBreakdown.forEach(item => {
          const costPercentage = totalCost > 0 ?
            ((Number(item.totalCost) / totalCost) * 100).toFixed(2) + '%' : '0.00%';

          csvContent += [
            `"${item.name || 'Unknown'}"`,
            `"${Number(item.volume).toFixed(2)}"`,
            `"${Number(item.unitCost).toFixed(4)}"`,
            `"${Number(item.totalCost).toFixed(2)}"`,
            `"${costPercentage}"`
          ].join(',') + '\n';
        });

        // Add total row
        if (costBreakdown.length > 0) {
          const totalVolume = costBreakdown.reduce((sum, item) => sum + Number(item.volume), 0);
          csvContent += [
            `"TOTAL SUMMARY"`,
            `"${totalVolume.toFixed(2)}"`,
            `"${totalVolume > 0 ? (totalCost / totalVolume).toFixed(4) : ''}"`,
            `"${totalCost.toFixed(2)}"`,
            `"100.00%"`
          ].join(',') + '\n';
        }

        csvContent += "\n";

        // SECTION 6: CHART DATA (Visualization)
        csvContent += "CHART DATA SECTION\n";
        csvContent += "Component Name,Volume,Percentage,Color Code\n";

        const chartData = Object.entries(result.blend || {})
          .filter(([_, volume]) => volume > 0)
          .map(([name, volume], index) => {
            const percentage = totalVolume > 0 ? ((volume / totalVolume) * 100) : 0;
            return {
              name,
              volume: Number(volume),
              percentage: percentage,
              color: SOLID_COLORS[index % SOLID_COLORS.length]
            };
          });

        chartData.forEach(item => {
          csvContent += [
            `"${item.name}"`,
            `"${item.volume.toFixed(2)}"`,
            `"${item.percentage.toFixed(2)}%"`,
            `"${item.color}"`
          ].join(',') + '\n';
        });

        // Add separator between multiple results
        if (resultIndex < resultsToExport.length - 1) {
          csvContent += "\n\n";
          csvContent += "========================================\n";
          csvContent += "NEXT OPTIMIZATION RESULT\n";
          csvContent += "========================================\n\n";
        }
      });

      // Create and download the CSV file
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);

      const fileName = selectedResult
        ? `Complete_Optimization_${(selectedResult.customName || 'result').replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
        : `All_Optimizations_${new Date().toISOString().split('T')[0]}.csv`;

      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      link.click();
      document.body.removeChild(link);

      addNotification("success", selectedResult
        ? `Complete CSV for "${selectedResult.customName}" exported successfully with Components, Properties and Cost Breakdown!`
        : "All optimizations exported successfully with complete data!");
    } catch (error) {
      console.error('Error exporting comprehensive CSV:', error);
      addNotification("error", `Failed to export CSV: ${error.message}`);
    }
  };

  const exportChartDataCSV = () => {
    if (!selectedResult) {
      addNotification("warning", "Please select a result first");
      return;
    }

    try {
      const chartData = prepareChartData(selectedResult.blend);
      const totalVolume = Object.values(selectedResult.blend || {}).reduce((a, b) => a + Number(b || 0), 0);

      const headers = ['Component Name', 'Volume', 'Percentage', 'Color'];
      const rows = chartData.map((item, index) => [
        item.name,
        item.volume,
        totalVolume > 0 ? ((item.volume / totalVolume) * 100).toFixed(2) + '%' : '0%',
        SOLID_COLORS[index % SOLID_COLORS.length]
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `chart_data_${formatDate(selectedResult.timestamp).replace(/[/:]/g, '-')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addNotification("success", "Chart data exported successfully");
    } catch (error) {
      console.error('Error exporting chart data:', error);
      addNotification("error", "Failed to export chart data");
    }
  };

  const getCurrentResults = () => {
    const filteredResults = savedResults.filter(result => {
      if (!resultsSearchTerm.trim()) return true;

      const searchLower = resultsSearchTerm.toLowerCase().trim();

      return (
        result.status?.toLowerCase().includes(searchLower) ||
        result.customName?.toLowerCase().includes(searchLower) ||
        result.customTextField?.toLowerCase().includes(searchLower) ||
        formatDate(result.timestamp).toLowerCase().includes(searchLower) ||
        result.totalCost?.toString().includes(searchLower) ||
        (result.blend && Object.keys(result.blend).some(key =>
          key.toLowerCase().includes(searchLower)
        ))
      );
    });

    const indexOfLastItem = resultsCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return {
      currentItems: filteredResults.slice(indexOfFirstItem, indexOfLastItem),
      totalItems: filteredResults.length,
      totalPages: Math.ceil(filteredResults.length / itemsPerPage)
    };
  };

  const getCurrentComponents = () => {
    const filteredAndSortedComponents = savedComponents
      .filter(component => {
        if (!searchTerm.trim()) return true;

        const searchLower = searchTerm.toLowerCase().trim();

        const nameMatch = component.components.some(comp =>
          comp.name.toLowerCase().includes(searchLower)
        );

        const costMatch = component.components.some(comp =>
          comp.cost?.toString().includes(searchLower)
        );

        const dateMatch = formatDate(component.timestamp)
          .toLowerCase()
          .includes(searchLower);

        const countMatch = component.components.length.toString().includes(searchLower);

        return nameMatch || costMatch || dateMatch || countMatch;
      })
      .sort((a, b) => {
        if (sortBy === 'newest') return b.timestamp - a.timestamp;
        if (sortBy === 'oldest') return a.timestamp - b.timestamp;
        if (sortBy === 'name') {
          const aName = a.components[0]?.name || '';
          const bName = b.components[0]?.name || '';
          return aName.localeCompare(bName);
        }
        return 0;
      });

    const indexOfLastItem = componentsCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return {
      currentItems: filteredAndSortedComponents.slice(indexOfFirstItem, indexOfLastItem),
      totalItems: filteredAndSortedComponents.length,
      totalPages: Math.ceil(filteredAndSortedComponents.length / itemsPerPage)
    };
  };

  const getCurrentProperties = () => {
    const filteredAndSortedProperties = savedProperties
      .filter(property => {
        if (!propertiesSearchTerm.trim()) return true;

        const searchLower = propertiesSearchTerm.toLowerCase().trim();

        const nameMatch = property.properties.some(prop =>
          prop.property.toLowerCase().includes(searchLower)
        );

        const uomMatch = property.properties.some(prop =>
          prop.uom?.toLowerCase().includes(searchLower)
        );

        const dateMatch = formatDate(property.timestamp)
          .toLowerCase()
          .includes(searchLower);

        const countMatch = property.properties.length.toString().includes(searchLower);

        return nameMatch || uomMatch || dateMatch || countMatch;
      })
      .sort((a, b) => b.timestamp - a.timestamp);

    const indexOfLastItem = propertiesCurrentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return {
      currentItems: filteredAndSortedProperties.slice(indexOfFirstItem, indexOfLastItem),
      totalItems: filteredAndSortedProperties.length,
      totalPages: Math.ceil(filteredAndSortedProperties.length / itemsPerPage)
    };
  };

  const handleResultsPageChange = (pageNumber) => {
    setResultsCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleComponentsPageChange = (pageNumber) => {
    setComponentsCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePropertiesPageChange = (pageNumber) => {
    setPropertiesCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, totalItems, onItemsPerPageChange }) => {
    const pageNumbers = [];
    const maxPageNumbers = isMobile ? 3 : 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

    if (endPage - startPage + 1 < maxPageNumbers) {
      startPage = Math.max(1, endPage - maxPageNumbers + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (totalPages <= 1) return null;

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="flex items-center gap-2 text-sm text-gray-700 flex-wrap justify-center">
          <span className="whitespace-nowrap">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
          >
            <option value={isMobile ? 4 : 6}>{isMobile ? 4 : 6}</option>
            <option value={12}>12</option>
            <option value={24}>24</option>
            <option value={48}>48</option>
          </select>
          <span className="ml-2 whitespace-nowrap text-xs sm:text-sm">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
          </span>
        </div>

        <div className="flex items-center gap-1 flex-wrap justify-center">
          <button
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-xs sm:text-sm"
          >
            First
          </button>

          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-xs sm:text-sm"
          >
            Prev
          </button>

          {startPage > 1 && (
            <span className="px-2 py-1 text-gray-500 text-xs sm:text-sm">...</span>
          )}

          {pageNumbers.map(number => (
            <button
              key={number}
              onClick={() => onPageChange(number)}
              className={`px-2 py-1 sm:px-3 sm:py-1 rounded-lg border transition-all text-xs sm:text-sm ${currentPage === number
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white border-transparent shadow-md'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:shadow-sm'
                }`}
            >
              {number}
            </button>
          ))}

          {endPage < totalPages && (
            <span className="px-2 py-1 text-gray-500 text-xs sm:text-sm">...</span>
          )}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-xs sm:text-sm"
          >
            Next
          </button>

          <button
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="px-2 py-1 sm:px-3 sm:py-1 rounded-lg border border-gray-300 bg-white text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-all text-xs sm:text-sm"
          >
            Last
          </button>
        </div>
      </div>
    );
  };

  const HighlightedText = ({ text, highlight }) => {
    if (!text || !highlight.trim()) {
      return <span>{text}</span>;
    }

    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.toString().split(regex);

    return (
      <span>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <mark key={i} className="bg-gradient-to-r from-yellow-200 to-yellow-300 px-1 rounded shadow-sm">{part}</mark>
          ) : (
            part
          )
        )}
      </span>
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 rounded-lg shadow-xl border border-gray-200 backdrop-blur-sm max-w-[200px]">
          <p className="font-semibold text-gray-900 text-sm break-words">{`${data.name || label}`}</p>
          <p className="text-sm text-gray-600">
            Volume: <span className="font-semibold text-blue-600">{payload[0].value.toFixed(2)}</span>
          </p>
          {data.percentage && (
            <p className="text-sm text-gray-600">
              Percentage: <span className="font-semibold text-green-600">{data.percentage.toFixed(2)}%</span>
            </p>
          )}
          {data.unitCost && (
            <p className="text-sm text-gray-600">
              Unit Cost: <span className="font-semibold text-purple-600">{getCurrencySymbol(data.currency)}{data.unitCost.toFixed(2)}</span>
            </p>
          )}
          {data.totalCost && (
            <p className="text-sm text-gray-600">
              Total Cost: <span className="font-semibold text-red-600">{getCurrencySymbol(data.currency)}{data.totalCost.toFixed(2)}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderEnhancedPieChart = (data) => (
    <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
      <PieChart>
        <defs>
          {data.map((entry, index) => (
            <linearGradient key={`gradient-${index}`} id={`colorGradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={SOLID_COLORS[index % SOLID_COLORS.length]} stopOpacity={0.8} />
              <stop offset="100%" stopColor={SOLID_COLORS[(index + 1) % SOLID_COLORS.length]} stopOpacity={0.8} />
            </linearGradient>
          ))}
        </defs>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={true}
          outerRadius={isMobile ? 80 : 120}
          innerRadius={isMobile ? 40 : 60}
          paddingAngle={2}
          dataKey="volume"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={`url(#colorGradient-${index})`}
              stroke="#fff"
              strokeWidth={2}
            />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
        <Legend
          verticalAlign="bottom"
          height={isMobile ? 60 : 36}
          formatter={(value, entry) => (
            <span style={{ color: '#374151', fontSize: isMobile ? '10px' : '12px' }}>
              {isMobile && value.length > 10 ? value.substring(0, 8) + '...' : value}
            </span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderEnhancedBarChart = (data) => (
    <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
      <BarChart data={data}>
        <defs>
          <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity={0.8} />
            <stop offset="100%" stopColor="#7c3aed" stopOpacity={0.8} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          angle={isMobile ? -90 : -45}
          textAnchor="end"
          height={isMobile ? 100 : 80}
          tick={{ fontSize: isMobile ? 10 : 12 }}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: isMobile ? 10 : 12 }}
          label={{ value: 'Volume', angle: -90, position: 'insideLeft', fontSize: isMobile ? 12 : 14 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar
          dataKey="volume"
          fill="url(#barGradient)"
          radius={[4, 4, 0, 0]}
          stroke="#fff"
          strokeWidth={1}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderEnhancedAreaChart = (data) => (
    <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          dataKey="name"
          angle={isMobile ? -90 : -45}
          textAnchor="end"
          height={isMobile ? 100 : 80}
          tick={{ fontSize: isMobile ? 10 : 12 }}
          interval={0}
        />
        <YAxis
          tick={{ fontSize: isMobile ? 10 : 12 }}
          label={{ value: 'Volume', angle: -90, position: 'insideLeft', fontSize: isMobile ? 12 : 14 }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="volume"
          stroke="#10b981"
          strokeWidth={3}
          fill="url(#areaGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  const renderRadialBarChart = (data) => {
    const validData = data.map(item => ({
      ...item,
      name: item.name || 'Unknown Component'
    }));

    return (
      <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
        <RadialBarChart
          innerRadius="20%"
          outerRadius="90%"
          data={validData}
          startAngle={180}
          endAngle={0}
        >
          <RadialBar
            minAngle={15}
            label={{
              position: 'insideStart',
              fill: '#fff',
              fontSize: isMobile ? 8 : 10,
              formatter: (value, entry) => ''
            }}
            background
            clockWise
            dataKey="volume"
          >
            {validData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={SOLID_COLORS[index % SOLID_COLORS.length]} />
            ))}
          </RadialBar>
          <Legend
            iconSize={isMobile ? 8 : 10}
            layout="vertical"
            verticalAlign="middle"
            wrapperStyle={{ right: isMobile ? 5 : 10 }}
            formatter={(value, entry) => (
              <span style={{ color: '#374151', fontSize: isMobile ? '10px' : '12px' }}>
                {isMobile && value.length > 10 ? value.substring(0, 8) + '...' : value}
              </span>
            )}
          />
          <Tooltip content={<CustomTooltip />} />
        </RadialBarChart>
      </ResponsiveContainer>
    );
  };

  const renderOverviewDashboard = () => {
    const totalVolume = Object.values(selectedResult.blend || {}).reduce((a, b) => a + Number(b || 0), 0);
    const componentCount = selectedResult.components?.length || 0;
    const propertyCount = selectedResult.propertySpecs?.length || 0;
    const activeComponents = Object.entries(selectedResult.blend || {}).filter(([_, volume]) => volume > 0).length;

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
            <div className="flex items-center gap-3">
              <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm`}>
                <svg className={`${isMobile ? 'w-5 h-5' : 'w-7 h-7'} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold`}>{selectedResult.status}</div>
                <div className="text-blue-100 font-medium text-xs sm:text-sm">Status</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
            <div className="flex items-center gap-3">
              <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm`}>
                <svg className={`${isMobile ? 'w-5 h-5' : 'w-7 h-7'} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </div>
              <div>
                <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold`}>{totalVolume.toLocaleString()}</div>
                <div className="text-green-100 font-medium text-xs sm:text-sm">Total Volume</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
            <div className="flex items-center gap-3">
              <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm`}>
                <svg className={`${isMobile ? 'w-5 h-5' : 'w-7 h-7'} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold`}>
                  {getCurrencySymbol(selectedResult.currency)}{selectedResult.totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-purple-100 font-medium text-xs sm:text-sm">Total Cost</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white shadow-xl">
            <div className="flex items-center gap-3">
              <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm`}>
                <svg className={`${isMobile ? 'w-5 h-5' : 'w-7 h-7'} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <div>
                <div className={`${isMobile ? 'text-lg' : 'text-2xl'} font-bold`}>{activeComponents}</div>
                <div className="text-orange-100 font-medium text-xs sm:text-sm">Active Components</div>
              </div>
            </div>
          </div>
        </div>

        <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Components</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Total Components</span>
                <span className="font-semibold text-gray-900 text-sm">{componentCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Used in Blend</span>
                <span className="font-semibold text-green-600 text-sm">{activeComponents}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Properties</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Total Properties</span>
                <span className="font-semibold text-gray-900 text-sm">{propertyCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Linear Properties</span>
                <span className="font-semibold text-blue-600 text-sm">
                  {selectedResult.propertySpecs?.filter(p => !p.index_formula && !p.reverse_formula).length || 0}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
            <div className="flex items-center gap-3 mb-3 sm:mb-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Cost Analysis</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Total Cost</span>
                <span className="font-semibold text-gray-900 text-sm">
                  {getCurrencySymbol(selectedResult.currency)}{selectedResult.totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 text-sm">Cost per Unit</span>
                <span className="font-semibold text-green-600 text-sm">
                  {getCurrencySymbol(selectedResult.currency)}{totalVolume > 0 ? (selectedResult.totalCost / totalVolume).toFixed(2) : '0.00'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Blend Composition Preview</h3>
          <div className="h-58 sm:h-70">
            {renderEnhancedPieChart(prepareEnhancedChartData(selectedResult.blend))}
          </div>
        </div>
      </div>
    );
  };

  const renderBlendView = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
        <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-3">
          <div className="w-2 h-6 sm:h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
          Blend Composition
        </h3>
        <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Component</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Volume</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Percentage</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Solution</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {Object.entries(selectedResult.blend || {})
                .filter(([_, volume]) => volume > 0)
                .map(([name, volume], index) => {
                  const totalVolume = Object.values(selectedResult.blend || {}).reduce((a, b) => a + Number(b || 0), 0);
                  const percentage = totalVolume > 0 ? (volume / totalVolume * 100) : 0;
                  return (
                    <tr key={index} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                            style={{ backgroundColor: SOLID_COLORS[index % SOLID_COLORS.length] }}
                          ></div>
                          <span className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[100px] sm:max-w-none">
                            <HighlightedText text={name} highlight={resultsSearchTerm} />
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-semibold">
                        {Number(volume).toFixed(2)}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                        <span className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {percentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-semibold">
                        <span className="inline-flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {Number(volume).toFixed(2)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderComponentsView = () => {
    const getPropertyColumns = (components) => {
      const fixedFields = new Set(["name", "originalname", "cost", "costunit", "min", "max", "availability", "volumeunit", "density", "gravity", "basis", "usedinsets", "blending_type", "blendingtype"]);
      const propertyColumns = new Set();

      components.forEach(component => {
        Object.keys(component).forEach(key => {
          const normalizedKey = String(key || "").toLowerCase();
          if (!fixedFields.has(normalizedKey) && normalizedKey) {
            propertyColumns.add(normalizedKey);
          }
        });
      });

      return Array.from(propertyColumns);
    };

    const selectedPropertyColumns = selectedResult.selectedPropertyColumns || [];
    const componentPropertyCols = selectedResult.components ? getPropertyColumns(selectedResult.components) : [];
    const allPropertyColumns = [...new Set([...selectedPropertyColumns, ...componentPropertyCols])];

    return (
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
        <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-3">
          <div className="w-2 h-6 sm:h-8 bg-gradient-to-b from-green-500 to-blue-600 rounded-full"></div>
          Blend Components
        </h3>
        <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Name</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Cost</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Min</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Max</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Availability</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Density</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Solution</th>
                {allPropertyColumns.map((prop, index) => (
                  <th key={index} className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                    {prop}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {selectedResult.components && selectedResult.components.map((component, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div
                        className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                        style={{ backgroundColor: SOLID_COLORS[index % SOLID_COLORS.length] }}
                      ></div>
                      <span className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[80px] sm:max-w-none">
                        <HighlightedText text={component.name} highlight={resultsSearchTerm} />
                      </span>
                    </div>
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-semibold">
                    <HighlightedText text={`${getCurrencySymbol(selectedResult.currency)}${Number(component.cost || 0).toFixed(2)}`} highlight={resultsSearchTerm} />
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                    {component.min || component.min === 0 ? component.min : '-'}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                    {component.max || component.max === 0 ? component.max : '-'}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                    {component.availability || component.availability === 0 ? component.availability : '-'}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                    {component.density || component.density === 0 ? component.density : '-'}
                  </td>
                  <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-semibold">
                    {selectedResult.blend && selectedResult.blend[component.name]
                      ? <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {Number(selectedResult.blend[component.name]).toFixed(2)}
                      </span>
                      : <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">0.00</span>}
                  </td>
                  {allPropertyColumns.map((prop, propIndex) => {
                    // Component already has property values from transformation
                    // Try exact match first, then case-insensitive match
                    let displayValue = component[prop];

                    if (displayValue === undefined || displayValue === null) {
                      // Try case-insensitive match
                      const matchingKey = Object.keys(component).find(key =>
                        key.toLowerCase() === prop.toLowerCase()
                      );
                      if (matchingKey) {
                        displayValue = component[matchingKey];
                      }
                    }

                    return (
                      <td key={propIndex} className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                        {displayValue !== undefined && displayValue !== null && displayValue !== ''
                          ? (typeof displayValue === 'number' ? Number(displayValue).toFixed(3) : String(displayValue))
                          : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPropertiesView = () => {
    const calculatePropertyResult = (prop) => {
      if (!selectedResult || !selectedResult.components || !selectedResult.blend) return 0;

      const blendData = selectedResult.blend;
      const components = selectedResult.components;

      if (prop.index_formula || prop.reverse_formula) {
        return prop.actualResult || 0;
      }

      let totalVolume = Object.values(blendData).reduce((sum, vol) => sum + Number(vol || 0), 0);
      if (totalVolume === 0) return 0;

      let weightedSum = 0;
      let totalWeightedVolume = 0;

      Object.entries(blendData).forEach(([name, volume]) => {
        const component = components.find(c =>
          c.name?.toLowerCase() === name?.toLowerCase()
        );

        if (component && volume > 0) {
          const compValue = component[prop.property] || 0;
          weightedSum += compValue * Number(volume);
          totalWeightedVolume += Number(volume);
        }
      });

      return totalWeightedVolume > 0 ? weightedSum / totalWeightedVolume : 0;
    };

    return (
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
        <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-3">
          <div className="w-2 h-6 sm:h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
          Property Constraints & Results
        </h3>
        <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Property</th>
                {!isMobile && (
                  <>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">UOM</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider">Min</th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider">Max</th>
                  </>
                )}
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Result (Min | Result | Max)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {selectedResult.propertySpecs && selectedResult.propertySpecs.map((prop, index) => {
                const actualResult = prop.actualResult !== undefined ? prop.actualResult : calculatePropertyResult(prop);
                const isWithinRange = actualResult >= (prop.min || 0) && actualResult <= (prop.max || 9999);

                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                          style={{ backgroundColor: SOLID_COLORS[index % SOLID_COLORS.length] }}
                        ></div>
                        <span className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[80px] sm:max-w-none">
                          <HighlightedText text={prop.property} highlight={resultsSearchTerm} />
                        </span>
                      </div>
                    </td>
                    {!isMobile && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {prop.uom || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {prop.min || prop.min === 0 ? prop.min : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                          {prop.max || prop.max === 0 ? prop.max : '-'}
                        </td>
                      </>
                    )}
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-1">
                        {!isMobile && (
                          <>
                            <span className="text-xs text-gray-600 font-medium">
                              {prop.min || 0}
                            </span>
                            <span className="text-gray-400">|</span>
                          </>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-medium ${isWithinRange
                          ? 'bg-green-100 text-green-800 border border-green-200'
                          : 'bg-red-100 text-red-800 border border-red-200'
                          }`}>
                          {actualResult.toFixed(3)}
                        </span>
                        {!isMobile && (
                          <>
                            <span className="text-gray-400">|</span>
                            <span className="text-xs text-gray-600 font-medium">
                              {prop.max || 0}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {isMobile && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 text-center">
              Switch to desktop view to see all property details including min/max constraints
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderCostView = () => {
    const generateCostBreakdown = () => {
      if (!selectedResult) return [];

      if (selectedResult.costBreakdown && selectedResult.costBreakdown.length > 0) {
        const validBreakdown = selectedResult.costBreakdown.filter(item =>
          item.name && item.name !== 'Unknown Component' && item.volume > 0
        );

        if (validBreakdown.length > 0) {
          return validBreakdown;
        }
      }

      const blendData = selectedResult.blend || {};
      const components = selectedResult.components || [];

      return Object.entries(blendData)
        .filter(([_, volume]) => volume > 0)
        .map(([name, volume]) => {
          const component = components.find(comp =>
            comp.name.toLowerCase() === name.toLowerCase()
          );
          return {
            name: name,
            volume: Number(volume),
            unitCost: component ? component.cost : 0,
            totalCost: component ? (component.cost * Number(volume)) : 0
          };
        });
    };

    const costBreakdown = generateCostBreakdown();
    const totalCost = costBreakdown.reduce((sum, item) => sum + (item.totalCost || 0), 0);

    return (
      <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
        <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center gap-3">
          <div className="w-2 h-6 sm:h-8 bg-gradient-to-b from-yellow-500 to-orange-600 rounded-full"></div>
          Cost Breakdown
        </h3>

        <div className={`grid gap-3 mb-4 sm:mb-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 sm:p-4 border border-blue-200">
            <div className="text-xs sm:text-sm text-blue-700 font-medium mb-1">Total Components</div>
            <div className="text-lg sm:text-2xl font-bold text-blue-800">{costBreakdown.length}</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 sm:p-4 border border-green-200">
            <div className="text-xs sm:text-sm text-green-700 font-medium mb-1">Total Volume</div>
            <div className="text-lg sm:text-2xl font-bold text-green-800">
              {costBreakdown.reduce((sum, item) => sum + (item.volume || 0), 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 sm:p-4 border border-purple-200 overflow-hidden">
            <div className="text-xs sm:text-sm text-purple-700 font-medium mb-1">Total Cost</div>
            <div className="text-lg sm:text-2xl font-bold text-purple-800 truncate break-words" title={totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}>
              {getCurrencySymbol(selectedResult.currency)}{totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Component</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Volume</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Unit Cost</th>
                <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">Total Cost</th>
                {!isMobile && (
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider">Percentage</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {costBreakdown.map((item, index) => {
                const percentage = totalCost > 0 ? ((item.totalCost / totalCost) * 100) : 0;

                return (
                  <tr key={index} className="hover:bg-gray-50 transition-colors group">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                          style={{ backgroundColor: SOLID_COLORS[index % SOLID_COLORS.length] }}
                        ></div>
                        <span className="text-xs sm:text-sm font-medium text-gray-900 group-hover:text-blue-600 transition-colors truncate max-w-[80px] sm:max-w-none">
                          <HighlightedText text={item.name} highlight={resultsSearchTerm} />
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right font-semibold">
                      {Number(item.volume).toFixed(2)}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 text-right">
                      <HighlightedText text={`${getCurrencySymbol(selectedResult.currency)}${Number(item.unitCost).toFixed(2)}`} highlight={resultsSearchTerm} />
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-gray-900 text-right">
                      <span className="bg-gradient-to-r from-green-100 to-green-200 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-green-800 border border-green-200 text-xs">
                        <HighlightedText text={`${getCurrencySymbol(selectedResult.currency)}${Number(item.totalCost).toFixed(2)}`} highlight={resultsSearchTerm} />
                      </span>
                    </td>
                    {!isMobile && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {percentage.toFixed(1)}%
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}

              <tr className="bg-gradient-to-r from-gray-50 to-gray-100 font-semibold border-t-2 border-gray-300">
                <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900">Total</td>
                <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 text-right">
                  {costBreakdown.reduce((sum, item) => sum + Number(item.volume), 0).toFixed(2)}
                </td>
                <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 text-right">-</td>
                <td className="px-3 sm:px-6 py-2 sm:py-4 text-xs sm:text-sm text-gray-900 text-right">
                  <span className="bg-gradient-to-r from-green-500 to-green-600 px-3 py-1 sm:px-4 sm:py-2 rounded-full text-white shadow-lg text-xs sm:text-sm">
                    {getCurrencySymbol(selectedResult.currency)}{totalCost.toFixed(2)}
                  </span>
                </td>
                {!isMobile && (
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">100%</td>
                )}
              </tr>
            </tbody>
          </table>
        </div>

        {costBreakdown.length > 0 && (
          <div className="mt-4 sm:mt-6 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Cost Distribution</h4>
            <div className="h-48 sm:h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={costBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={isMobile ? 60 : 80}
                    innerRadius={isMobile ? 30 : 40}
                    paddingAngle={2}
                    dataKey="totalCost"
                  >
                    {costBreakdown.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={SOLID_COLORS[index % SOLID_COLORS.length]}
                        stroke="#fff"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [`${getCurrencySymbol(selectedResult.currency)}${Number(value).toFixed(2)}`, 'Cost']}
                    labelFormatter={(name) => `Component: ${name}`}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderVisualizationView = () => {
    const chartData = prepareEnhancedChartData(selectedResult.blend || {});

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
            <button
              onClick={() => setChartType('pie')}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all transform hover:scale-105 text-xs sm:text-sm ${chartType === 'pie'
                ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Pie
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all transform hover:scale-105 text-xs sm:text-sm ${chartType === 'bar'
                ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Bar
            </button>
            <button
              onClick={() => setChartType('area')}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all transform hover:scale-105 text-xs sm:text-sm ${chartType === 'area'
                ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Area
            </button>
            <button
              onClick={() => setChartType('radial')}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all transform hover:scale-105 text-xs sm:text-sm ${chartType === 'radial'
                ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Radial
            </button>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 border border-gray-200">
            {chartType === 'pie' && renderEnhancedPieChart(chartData)}
            {chartType === 'bar' && renderEnhancedBarChart(chartData)}
            {chartType === 'area' && renderEnhancedAreaChart(chartData)}
            {chartType === 'radial' && renderRadialBarChart(chartData)}
          </div>
        </div>

        <div className={`grid gap-3 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 sm:p-4 text-white text-center shadow-xl">
            <div className="text-lg sm:text-2xl font-bold">{chartData.length}</div>
            <div className="text-blue-100 text-xs sm:text-sm">Components</div>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-3 sm:p-4 text-white text-center shadow-xl">
            <div className="text-lg sm:text-2xl font-bold">
              {chartData.reduce((sum, item) => sum + item.volume, 0).toLocaleString()}
            </div>
            <div className="text-green-100 text-xs sm:text-sm">Total Volume</div>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 sm:p-4 text-white text-center shadow-xl">
            <div className="text-lg sm:text-2xl font-bold">
              {Math.max(...chartData.map(item => item.percentage)).toFixed(1)}%
            </div>
            <div className="text-purple-100 text-xs sm:text-sm">Largest Share</div>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 sm:p-4 text-white text-center shadow-xl">
            <div className="text-lg sm:text-2xl font-bold">
              {Math.min(...chartData.map(item => item.percentage)).toFixed(1)}%
            </div>
            <div className="text-orange-100 text-xs sm:text-sm">Smallest Share</div>
          </div>
        </div>
      </div>
    );
  };

  const renderComponentCard = (componentData) => {
    const getPropertyColumns = (components) => {
      const fixedFields = new Set(["name", "originalname", "cost", "costunit", "min", "max", "availability", "volumeunit", "density", "gravity", "basis", "usedinsets", "blending_type", "blendingtype"]);
      const propertyColumns = new Set();

      components.forEach(component => {
        Object.keys(component).forEach(key => {
          const normalizedKey = String(key || "").toLowerCase();
          if (!fixedFields.has(normalizedKey) && normalizedKey) {
            propertyColumns.add(normalizedKey);
          }
        });
      });

      return Array.from(propertyColumns);
    };

    const propertyCols = getPropertyColumns(componentData.components);

    return (
      <div
        key={componentData._id}
        className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105 group"
        onClick={() => {
          setSelectedComponent(componentData);
          setDetailView(true);
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                <HighlightedText
                  text={componentData.customName || `Component Set`}
                  highlight={searchTerm}
                />
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">
                <HighlightedText text={formatDate(componentData.timestamp)} highlight={searchTerm} />
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditComponent(componentData);
              }}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                applyComponentsToOptimizer(componentData);
              }}
              disabled={loading.applyComponents}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-xl font-medium transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              {loading.applyComponents ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Apply
                </>
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Are you sure you want to delete this component set? This action cannot be undone.')) {
                  handleDeleteComponent(componentData._id);
                }
              }}
              disabled={loading.deleteComponent}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              {loading.deleteComponent ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </>
              )}
            </button>
          </div>
        </div>

        <div className={`grid gap-2 sm:gap-3 text-center mb-3 sm:mb-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-2 sm:p-3 border border-blue-200">
            <div className="text-base sm:text-xl font-bold text-blue-600">
              <HighlightedText text={componentData.components.length.toString()} highlight={searchTerm} />
            </div>
            <div className="text-xs text-blue-700 font-medium">Components</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-2 sm:p-3 border border-green-200">
            <div className="text-sm sm:text-lg font-bold text-green-600">
              <HighlightedText
                text={`${getCurrencySymbol(componentData.currency || currency)}${componentData.components.reduce((sum, comp) => sum + (Number(comp.cost) || 0), 0).toFixed(2)}`}
                highlight={searchTerm}
              />
            </div>
            <div className="text-xs text-green-700 font-medium">Total Cost</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-2 sm:p-3 border border-purple-200">
            <div className="text-sm sm:text-lg font-bold text-purple-600">
              <HighlightedText
                text={componentData.components.filter(comp => comp.min > 0).length.toString()}
                highlight={searchTerm}
              />
            </div>
            <div className="text-xs text-purple-700 font-medium">Min Constraints</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-2 sm:p-3 border border-orange-200">
            <div className="text-sm sm:text-lg font-bold text-orange-600">
              <HighlightedText
                text={componentData.components.filter(comp => comp.max > 0).length.toString()}
                highlight={searchTerm}
              />
            </div>
            <div className="text-xs text-orange-700 font-medium">Max Constraints</div>
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
            Components:
          </h4>
          <div className="flex flex-wrap gap-1">
            {componentData.components.slice(0, isMobile ? 3 : 5).map((comp, compIndex) => (
              <span key={compIndex} className="bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-2 py-1 rounded-lg text-xs border border-blue-200 shadow-sm truncate max-w-[80px] sm:max-w-none">
                <HighlightedText text={comp.name} highlight={searchTerm} />
                {comp.cost && <span className="text-blue-600 ml-1">({getCurrencySymbol(componentData.currency || currency)}{comp.cost})</span>}
              </span>
            ))}
            {componentData.components.length > (isMobile ? 3 : 5) && (
              <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 px-2 py-1 rounded-lg text-xs border border-gray-200 shadow-sm">
                +{componentData.components.length - (isMobile ? 3 : 5)} more
              </span>
            )}
          </div>
        </div>

        {propertyCols.length > 0 && (
          <div>
            <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
              Custom Properties:
            </h4>
            <div className="flex flex-wrap gap-1">
              {propertyCols.slice(0, isMobile ? 3 : 5).map((prop, propIndex) => (
                <span key={propIndex} className="bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 px-2 py-1 rounded-lg text-xs border border-purple-200 shadow-sm truncate max-w-[80px] sm:max-w-none">
                  {prop}
                </span>
              ))}
              {propertyCols.length > (isMobile ? 3 : 5) && (
                <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 px-2 py-1 rounded-lg text-xs border border-gray-200 shadow-sm">
                  +{propertyCols.length - (isMobile ? 3 : 5)} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPropertyCard = (propertyData) => {
    return (
      <div
        key={propertyData._id}
        className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105 group"
        onClick={() => {
          setSelectedProperty(propertyData);
          setPropertyDetailView(true);
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 sm:mb-4 gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
                <HighlightedText
                  text={propertyData.customName || `Property Set`}
                  highlight={propertiesSearchTerm}
                />
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm">
                <HighlightedText text={formatDate(propertyData.timestamp)} highlight={propertiesSearchTerm} />
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditProperty(propertyData);
              }}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                applyPropertiesToOptimizer(propertyData);
              }}
              disabled={loading.applyProperties}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-xl font-medium transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              {loading.applyProperties ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Apply
                </>
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm('Are you sure you want to delete this property set? This action cannot be undone.')) {
                  handleDeleteProperty(propertyData._id);
                }
              }}
              disabled={loading.deleteProperty}
              className="px-2 py-1 sm:px-3 sm:py-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all text-xs flex items-center gap-1 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
            >
              {loading.deleteProperty ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </>
              )}
            </button>
          </div>
        </div>

        <div className={`grid gap-2 sm:gap-3 text-center mb-3 sm:mb-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-2 md:grid-cols-4'}`}>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-2 sm:p-3 border border-blue-200">
            <div className="text-base sm:text-xl font-bold text-blue-600">
              <HighlightedText text={propertyData.properties.length.toString()} highlight={propertiesSearchTerm} />
            </div>
            <div className="text-xs text-blue-700 font-medium">Properties</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-2 sm:p-3 border border-green-200">
            <div className="text-sm sm:text-lg font-bold text-green-600">
              <HighlightedText
                text={propertyData.properties.filter(prop => !prop.index_formula && !prop.reverse_formula).length.toString()}
                highlight={propertiesSearchTerm}
              />
            </div>
            <div className="text-xs text-green-700 font-medium">Linear</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-2 sm:p-3 border border-purple-200">
            <div className="text-sm sm:text-lg font-bold text-purple-600">
              <HighlightedText
                text={propertyData.properties.filter(prop => prop.index_formula || prop.reverse_formula).length.toString()}
                highlight={propertiesSearchTerm}
              />
            </div>
            <div className="text-xs text-purple-700 font-medium">Non-Linear</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-2 sm:p-3 border border-orange-200">
            <div className="text-sm sm:text-lg font-bold text-orange-600">
              <HighlightedText
                text={[...new Set(propertyData.properties.map(prop => prop.basis))].length.toString()}
                highlight={propertiesSearchTerm}
              />
            </div>
            <div className="text-xs text-orange-700 font-medium">Basis Types</div>
          </div>
        </div>

        <div>
          <h4 className="text-xs sm:text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
            Top Properties:
          </h4>
          <div className="flex flex-wrap gap-1">
            {propertyData.properties.slice(0, isMobile ? 3 : 5).map((prop, propIndex) => {
              const hasFormula = prop.index_formula || prop.reverse_formula;
              const blendingType = hasFormula ? 'Non-Linear' : 'Linear';

              return (
                <span key={propIndex} className={`px-2 py-1 rounded-lg text-xs border shadow-sm truncate max-w-[80px] sm:max-w-none ${blendingType === 'Linear'
                  ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-200'
                  : 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-200'
                  }`}>
                  <HighlightedText text={prop.property} highlight={propertiesSearchTerm} />
                  {prop.uom && <span className="ml-1">({prop.uom})</span>}
                </span>
              );
            })}
            {propertyData.properties.length > (isMobile ? 3 : 5) && (
              <span className="bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 px-2 py-1 rounded-lg text-xs border border-gray-200 shadow-sm">
                +{propertyData.properties.length - (isMobile ? 3 : 5)} more
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderComponentDetailView = () => {
    const getPropertyColumns = (components) => {
      const fixedFields = new Set(["name", "originalname", "cost", "costunit", "min", "max", "availability", "volumeunit", "density", "gravity", "basis", "usedinsets", "blending_type", "blendingtype"]);
      const propertyColumns = new Set();

      components.forEach(component => {
        Object.keys(component).forEach(key => {
          const normalizedKey = String(key || "").toLowerCase();
          if (!fixedFields.has(normalizedKey) && normalizedKey) {
            propertyColumns.add(normalizedKey);
          }
        });
      });

      return Array.from(propertyColumns);
    };

    const propertyCols = getPropertyColumns(selectedComponent.components);

    return (
      <div className="max-w-7xl mx-auto w-full px-2 sm:px-0">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setDetailView(false)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mb-3 sm:mb-4 text-sm sm:text-base group transition-colors"
          >
            <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Components List
          </button>

          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-4">
              <div className="flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 break-words bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {selectedComponent.customName || `Component Set`}
                </h1>
                <p className="text-gray-600 mt-2 text-xs sm:text-base">{formatDate(selectedComponent.timestamp)}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => applyComponentsToOptimizer(selectedComponent)}
                  disabled={loading.applyComponents}
                  className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-xl font-medium transition-all text-xs sm:text-sm flex items-center gap-2 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
                >
                  {loading.applyComponents ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Apply to Optimizer
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this component set? This action cannot be undone.')) {
                      handleDeleteComponent(selectedComponent._id);
                    }
                  }}
                  disabled={loading.deleteComponent}
                  className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all text-xs sm:text-sm flex items-center gap-2 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
                >
                  {loading.deleteComponent ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Component Name
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Min
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Max
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Availability
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-right text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Density
                    </th>
                    {!isMobile && propertyCols.map((prop, index) => (
                      <th key={index} className="px-6 py-4 text-right text-sm font-semibold text-gray-700 uppercase tracking-wider">
                        {prop.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedComponent.components.map((component, compIndex) => (
                    <tr key={compIndex} className="hover:bg-gray-50 transition-colors group">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div
                            className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                            style={{ backgroundColor: SOLID_COLORS[compIndex % SOLID_COLORS.length] }}
                          ></div>
                          <span className="font-medium text-gray-900 text-xs sm:text-sm group-hover:text-blue-600 transition-colors truncate max-w-[80px] sm:max-w-none">
                            <HighlightedText text={component.name} highlight={searchTerm} />
                          </span>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 font-semibold text-xs sm:text-sm bg-gradient-to-r from-green-100 to-green-200 px-2 py-1 rounded-lg">
                          <HighlightedText text={`${getCurrencySymbol(selectedComponent.currency || currency)}${Number(component.cost || 0).toFixed(2)}`} highlight={searchTerm} />
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 text-xs sm:text-sm">
                          {component.min || component.min === 0 ? component.min : '-'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 text-xs sm:text-sm">
                          {component.max || component.max === 0 ? component.max : '-'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 text-xs sm:text-sm">
                          {component.availability || component.availability === 0 ? component.availability : '-'}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right">
                        <span className="text-gray-900 text-xs sm:text-sm">
                          {component.density || component.density === 0 ? component.density : '-'}
                        </span>
                      </td>
                      {!isMobile && propertyCols.map((prop, propIndex) => (
                        <td key={propIndex} className="px-6 py-4 whitespace-nowrap text-right">
                          <span className="text-gray-900 text-sm bg-gray-50 px-2 py-1 rounded-lg">
                            {component[prop] || component[prop] === 0 ? component[prop] : '-'}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-gradient-to-r from-gray-50 to-gray-100 font-semibold border-t-2 border-gray-300">
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-gray-900 text-xs sm:text-sm">
                      Total Cost
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right text-gray-900 text-xs sm:text-sm">
                      <span className="bg-gradient-to-r from-green-500 to-green-600 text-white px-2 py-1 sm:px-3 sm:py-1 rounded-full shadow-lg text-xs sm:text-sm">
                        {getCurrencySymbol(selectedComponent.currency || currency)}{selectedComponent.components.reduce((sum, comp) => sum + (Number(comp.cost) || 0), 0).toFixed(2)}
                      </span>
                    </td>
                    <td colSpan={4 + (isMobile ? 0 : propertyCols.length)}></td>
                  </tr>
                </tbody>
              </table>
            </div>
            {isMobile && propertyCols.length > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 text-center">
                  {propertyCols.length} custom properties available - switch to desktop view to see all columns
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPropertyDetailView = () => {
    return (
      <div className="max-w-7xl mx-auto w-full px-2 sm:px-0">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => setPropertyDetailView(false)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium mb-3 sm:mb-4 text-sm sm:text-base group transition-colors"
          >
            <svg className="w-4 h-4 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Properties List
          </button>

          <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-4">
              <div className="flex-1">
                <h1 className="text-xl sm:text-3xl font-bold text-gray-900 break-words bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {selectedProperty.customName || `Property Set`}
                </h1>
                <p className="text-gray-600 mt-2 text-xs sm:text-base">{formatDate(selectedProperty.timestamp)}</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => applyPropertiesToOptimizer(selectedProperty)}
                  disabled={loading.applyProperties}
                  className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 rounded-xl font-medium transition-all text-xs sm:text-sm flex items-center gap-2 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
                >
                  {loading.applyProperties ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Apply to Optimizer
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this property set? This action cannot be undone.')) {
                      handleDeleteProperty(selectedProperty._id);
                    }
                  }}
                  disabled={loading.deleteProperty}
                  className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl font-medium transition-all text-xs sm:text-sm flex items-center gap-2 flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
                >
                  {loading.deleteProperty ? (
                    <>
                      <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg sm:rounded-xl border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-left text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Property Name
                    </th>
                    {!isMobile && (
                      <>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          UOM
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Basis
                        </th>
                      </>
                    )}
                    <th className="px-3 sm:px-6 py-2 sm:py-4 text-center text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      Type
                    </th>
                    {!isMobile && (
                      <>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Index Formula
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">
                          Reverse Formula
                        </th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {selectedProperty.properties.map((property, propIndex) => {
                    const hasFormula = property.index_formula || property.reverse_formula;
                    const blendingType = hasFormula ? 'Non-Linear' : 'Linear';

                    return (
                      <tr key={propIndex} className="hover:bg-gray-50 transition-colors group">
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div
                              className="w-2 h-2 sm:w-3 sm:h-3 rounded-full mr-2 sm:mr-3 shadow-sm"
                              style={{ backgroundColor: SOLID_COLORS[propIndex % SOLID_COLORS.length] }}
                            ></div>
                            <span className="font-medium text-gray-900 text-xs sm:text-sm group-hover:text-purple-600 transition-colors truncate max-w-[80px] sm:max-w-none">
                              <HighlightedText text={property.property} highlight={propertiesSearchTerm} />
                            </span>
                          </div>
                        </td>
                        {!isMobile && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-gray-900 text-sm bg-gray-50 px-2 py-1 rounded-lg">
                                {property.uom || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-gray-900 text-sm bg-blue-50 px-2 py-1 rounded-lg">
                                {property.basis || 'volume'}
                              </span>
                            </td>
                          </>
                        )}
                        <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-center">
                          <span className={`inline-flex items-center px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-medium border ${blendingType === 'Linear'
                            ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-200'
                            : 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border-purple-200'
                            }`}>
                            {isMobile ? (blendingType === 'Linear' ? 'Lin' : 'Non-Lin') : blendingType}
                          </span>
                        </td>
                        {!isMobile && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-gray-900 text-sm font-mono bg-gray-50 px-2 py-1 rounded-lg">
                                {property.index_formula || '-'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-gray-900 text-sm font-mono bg-gray-50 px-2 py-1 rounded-lg">
                                {property.reverse_formula || '-'}
                              </span>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {isMobile && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-600 text-center">
                  Switch to desktop view to see all property details including formulas
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderResultsPage = () => {
    const { currentItems: currentResults, totalItems: totalResults, totalPages: totalResultsPages } = getCurrentResults();

    return (
      <div className="max-w-7xl mx-auto w-full px-2 sm:px-0">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Optimization Results
              </h1>
              <p className="text-gray-600 mt-2 text-xs sm:text-base">View and analyze your saved optimization results from MongoDB</p>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              {savedResults.length > 0 && (
                <>


                  <button
                    onClick={handleExportResultsCSV}
                    className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-medium hover:from-purple-600 hover:to-purple-700 transition-all flex items-center gap-2 text-xs sm:text-base flex-shrink-0 transform hover:scale-105 shadow-lg"
                    title={selectedResult ? `Download CSV for "${selectedResult.customName}"` : "Download CSV for all results"}
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    CSV
                    {selectedResult && (
                      <span className="bg-white text-purple-600 text-xs px-1 py-0.5 rounded-full">
                        Selected
                      </span>
                    )}
                  </button>

                </>
              )}
            </div>
          </div>
        </div>

        {loading.results ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl">
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse"></div>
                </div>
              </div>
              <span className="ml-3 sm:ml-4 text-base sm:text-lg text-gray-600 font-medium">Loading results from database...</span>
            </div>
          </div>
        ) : savedResults.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No Saved Results</h3>
            <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-lg">
              No optimization results found in MongoDB database. Go back to the optimizer and run some optimizations to see results here.
            </p>
            <button
              onClick={() => onTabChange('optimizer')}
              className="px-6 py-3 sm:px-8 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-base sm:text-lg"
            >
              Back to Optimizer
            </button>
          </div>
        ) : (
          <>
            <div className={`gap-4 sm:gap-6 ${isMobile ? 'flex flex-col' : 'grid grid-cols-1 lg:grid-cols-4'}`}>
              {!isMobile && (
                <div className="lg:col-span-1">
                  <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
                    <div className="mb-4">
                      <div className="relative">
                        <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                          type="text"
                          placeholder="Search results..."
                          value={resultsSearchTerm}
                          onChange={(e) => setResultsSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base bg-gray-50"
                        />
                      </div>
                    </div>

                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      Saved Results ({totalResults})
                    </h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {currentResults.map((result) => (
                        <div
                          key={result._id}
                          className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${selectedResult && selectedResult._id === result._id
                            ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 shadow-lg'
                            : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:shadow-lg'
                            }`}
                          onClick={() => {
                            setSelectedResult(result);
                            setActiveView('overview');
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-xs sm:text-sm mb-1 truncate">
                                <HighlightedText
                                  text={result.customName || result.customTextField || result.status || 'Optimization Result'}
                                  highlight={resultsSearchTerm}
                                />
                              </div>
                              <div className="text-xs text-gray-600 mb-2">
                                <HighlightedText text={formatDate(result.timestamp)} highlight={resultsSearchTerm} />
                              </div>
                              <div className="text-sm font-bold text-green-600">
                                <HighlightedText
                                  text={`${getCurrencySymbol(result.currency)}${result.totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                  highlight={resultsSearchTerm}
                                />
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                {result.components?.length || 0} components, {result.propertySpecs?.length || 0} properties
                              </div>
                            </div>
                            <div className="flex gap-1 ml-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditResult(result);
                                }}
                                className="text-gray-400 hover:text-blue-600 p-1 transition-colors flex-shrink-0 transform hover:scale-110"
                                title="Edit this result"
                              >
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteResult(result._id);
                                }}
                                disabled={loading.deleteResult}
                                className="text-gray-400 hover:text-red-600 p-1 transition-colors flex-shrink-0 transform hover:scale-110 disabled:opacity-50"
                              >
                                {loading.deleteResult ? (
                                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {currentResults.length === 0 && (
                        <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-xl border border-gray-200">
                          No results found for "{resultsSearchTerm}"
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className={`${isMobile ? 'w-full' : 'lg:col-span-3'}`}>
                {selectedResult && (
                  <div className="space-y-4 sm:space-y-6">
                    <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-4">
                        <div className="flex-1">
                          <h2 className="text-xl sm:text-3xl font-bold text-gray-900 break-words bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            <HighlightedText
                              text={selectedResult.customName || selectedResult.customTextField || selectedResult.status}
                              highlight={resultsSearchTerm}
                            />
                          </h2>
                          <p className="text-gray-600 mt-2 text-sm sm:text-base">
                            <HighlightedText text={formatDate(selectedResult.timestamp)} highlight={resultsSearchTerm} />
                          </p>
                          <div className="flex gap-3 sm:gap-4 mt-3 text-xs sm:text-sm text-gray-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                              {selectedResult.components?.length || 0} Components
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-purple-500 rounded-full"></div>
                              {selectedResult.propertySpecs?.length || 0} Properties
                            </span>
                            <span className="flex items-center gap-1">
                              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                              Total Volume: {Object.values(selectedResult.blend || {}).reduce((a, b) => a + Number(b || 0), 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => applyAllToOptimizer(selectedResult)}
                            disabled={loading.applyAll}
                            className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-600 text-white hover:from-blue-700 hover:to-blue-700 rounded-xl font-medium transition-all flex items-center gap-2 text-xs sm:text-sm flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
                          >
                            {loading.applyAll ? (
                              <>
                                <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Loading...
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Apply All
                              </>
                            )}
                          </button>

                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setActiveView('overview')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'overview'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Overview' : 'Overview'}
                        </button>
                        <button
                          onClick={() => setActiveView('blend')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'blend'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Blend' : 'Blend Results'}
                        </button>
                        <button
                          onClick={() => setActiveView('components')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'components'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Comps' : 'Components'}
                        </button>
                        <button
                          onClick={() => setActiveView('properties')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'properties'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Props' : 'Properties'}
                        </button>
                        <button
                          onClick={() => setActiveView('cost')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'cost'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Cost' : 'Cost Breakdown'}
                        </button>
                        <button
                          onClick={() => setActiveView('visualization')}
                          className={`px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-medium transition-all text-xs sm:text-sm transform hover:scale-105 ${activeView === 'visualization'
                            ? 'bg-gradient-to-r from-blue-600 to-blue-600 text-white shadow-lg'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                          {isMobile ? 'Chart' : 'Visualization'}
                        </button>
                      </div>
                    </div>

                    {activeView === 'overview' && renderOverviewDashboard()}
                    {activeView === 'blend' && renderBlendView()}
                    {activeView === 'components' && renderComponentsView()}
                    {activeView === 'properties' && renderPropertiesView()}
                    {activeView === 'cost' && renderCostView()}
                    {activeView === 'visualization' && renderVisualizationView()}
                  </div>
                )}
              </div>

              {isMobile && (
                <div className="bg-white rounded-xl p-4 shadow-2xl border border-gray-100">
                  <div className="mb-4">
                    <div className="relative">
                      <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="text"
                        placeholder="Search results..."
                        value={resultsSearchTerm}
                        onChange={(e) => setResultsSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm bg-gray-50"
                      />
                    </div>
                  </div>

                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                    Saved Results ({totalResults})
                  </h3>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {currentResults.map((result) => (
                      <div
                        key={result._id}
                        className={`p-3 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${selectedResult && selectedResult._id === result._id
                          ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 shadow-lg'
                          : 'bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:shadow-lg'
                          }`}
                        onClick={() => {
                          setSelectedResult(result);
                          setActiveView('overview');
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-xs mb-1 truncate">
                              <HighlightedText
                                text={result.customName || result.customTextField || result.status || 'Optimization Result'}
                                highlight={resultsSearchTerm}
                              />
                            </div>
                            <div className="text-xs text-gray-600 mb-1">
                              <HighlightedText text={formatDate(result.timestamp)} highlight={resultsSearchTerm} />
                            </div>
                            <div className="text-xs font-bold text-green-600">
                              <HighlightedText
                                text={`${getCurrencySymbol(result.currency)}${result.totalCost?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                highlight={resultsSearchTerm}
                              />
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteResult(result._id);
                              }}
                              disabled={loading.deleteResult}
                              className="text-gray-400 hover:text-red-600 p-1 transition-colors flex-shrink-0 transform hover:scale-110 disabled:opacity-50"
                            >
                              {loading.deleteResult ? (
                                <div className="w-3 h-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {currentResults.length === 0 && (
                      <div className="text-center py-3 text-gray-500 text-xs bg-gray-50 rounded-xl border border-gray-200">
                        No results found for "{resultsSearchTerm}"
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {totalResultsPages > 1 && (
              <Pagination
                currentPage={resultsCurrentPage}
                totalPages={totalResultsPages}
                onPageChange={handleResultsPageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalResults}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderComponentsPage = () => {
    if (detailView && selectedComponent) {
      return renderComponentDetailView();
    }

    const { currentItems: currentComponents, totalItems: totalComponents, totalPages: totalComponentsPages } = getCurrentComponents();

    return (
      <div className="max-w-7xl mx-auto w-full px-2 sm:px-0">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Saved Components
              </h1>
              <p className="text-gray-600 mt-2 text-xs sm:text-base">Manage and export your saved blend components from MongoDB</p>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => onTabChange('optimizer')}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium hover:from-gray-700 hover:to-gray-800 transition-all flex items-center gap-2 text-xs sm:text-base flex-shrink-0 transform hover:scale-105 shadow-lg"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Optimizer
              </button>

              <button
                onClick={loadComponentsFromMongoDB}
                disabled={loading.components}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all flex items-center gap-2 text-xs sm:text-base flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
              >
                {loading.components ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {loading.components ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl">
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse"></div>
                </div>
              </div>
              <span className="ml-3 sm:ml-4 text-base sm:text-lg text-gray-600 font-medium">Loading components from database...</span>
            </div>
          </div>
        ) : savedComponents.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl mt-6 sm:mt-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No components saved yet</h3>
            <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-lg">
              No components found in MongoDB database. Start by creating your first component set in the optimizer.
            </p>
            <button
              onClick={() => onTabChange('optimizer')}
              className="px-6 py-3 sm:px-8 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-base sm:text-lg"
            >
              Go to Optimizer
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100 mb-4 sm:mb-6">
              <div className="flex flex-col md:flex-row gap-3 sm:gap-4 md:items-center">
                <div className="flex-1">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search components by name, cost, date..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base bg-gray-50"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-2 py-2 sm:px-3 sm:py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base bg-gray-50"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="name">Name</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={`gap-4 sm:gap-6 ${isMobile ? 'grid grid-cols-1' : 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'}`}>
              {currentComponents.map((componentData) =>
                renderComponentCard(componentData)
              )}
            </div>

            {totalComponentsPages > 1 && (
              <Pagination
                currentPage={componentsCurrentPage}
                totalPages={totalComponentsPages}
                onPageChange={handleComponentsPageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalComponents}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const renderPropertiesPage = () => {
    if (propertyDetailView && selectedProperty) {
      return renderPropertyDetailView();
    }

    const { currentItems: currentProperties, totalItems: totalProperties, totalPages: totalPropertiesPages } = getCurrentProperties();

    return (
      <div className="max-w-7xl mx-auto w-full px-2 sm:px-0">
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Saved Properties
              </h1>
              <p className="text-gray-600 mt-2 text-xs sm:text-base">Manage and export your saved property constraints from MongoDB</p>
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-3">
              <button
                onClick={() => onTabChange('optimizer')}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl font-medium hover:from-gray-700 hover:to-gray-800 transition-all flex items-center gap-2 text-xs sm:text-base flex-shrink-0 transform hover:scale-105 shadow-lg"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Optimizer
              </button>

              <button
                onClick={loadPropertiesFromMongoDB}
                disabled={loading.properties}
                className="px-3 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-all flex items-center gap-2 text-xs sm:text-base flex-shrink-0 transform hover:scale-105 shadow-lg disabled:opacity-50"
              >
                {loading.properties ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {loading.properties ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl">
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full animate-pulse"></div>
                </div>
              </div>
              <span className="ml-3 sm:ml-4 text-base sm:text-lg text-gray-600 font-medium">Loading properties from database...</span>
            </div>
          </div>
        ) : savedProperties.length === 0 ? (
          <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center border border-gray-100 shadow-2xl mt-6 sm:mt-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl sm:rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">No properties saved yet</h3>
            <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-lg">
              No property sets found in MongoDB database. Start by creating your first property set in the optimizer.
            </p>
            <button
              onClick={() => onTabChange('optimizer')}
              className="px-6 py-3 sm:px-8 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-medium hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-base sm:text-lg"
            >
              Go to Optimizer
            </button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-2xl border border-gray-100 mb-4 sm:mb-6">
              <div className="flex flex-col md:flex-row gap-3 sm:gap-4 md:items-center">
                <div className="flex-1">
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search properties by name, UOM, date..."
                      value={propertiesSearchTerm}
                      onChange={(e) => setPropertiesSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 sm:py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm sm:text-base bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={`gap-4 sm:gap-6 ${isMobile ? 'grid grid-cols-1' : 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'}`}>
              {currentProperties.map((propertyData) =>
                renderPropertyCard(propertyData)
              )}
            </div>

            {totalPropertiesPages > 1 && (
              <Pagination
                currentPage={propertiesCurrentPage}
                totalPages={totalPropertiesPages}
                onPageChange={handlePropertiesPageChange}
                itemsPerPage={itemsPerPage}
                totalItems={totalProperties}
                onItemsPerPageChange={setItemsPerPage}
              />
            )}
          </>
        )}
      </div>
    );
  };

  if (!currentUser) {
    return <Login onLogin={() => { }} users={[]} setUsers={() => { }} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 flex">
      <NotificationToaster notifications={notifications} />

      <Navigation
        currentUser={currentUser}
        activeTab={activeTab}
        onTabChange={onTabChange}
        onLogout={handleLogoutClick}
        onShowUserManagement={onShowUserManagement}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar}
      />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
        <Header
          currentUser={currentUser}
          onTestAPI={() => { }}
          onToggleSidebar={onToggleSidebar}
        />

        <main className="flex-1 p-3 sm:p-4 sm:p-6 overflow-auto">
          {activeTab === 'components' ? renderComponentsPage() :
            activeTab === 'properties' ? renderPropertiesPage() :
              renderResultsPage()}
        </main>
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutConfirm}
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />
    </div>
  );
};

export default ComponentsAndResultsPage;